/**
 * Mana Locals Admin — M01 ONBOARDING LIFECYCLE boundary schemas.
 *
 * CONTRACT SOURCE: `contracts/openapi/foundation.v1.yaml` (API repository),
 * contract register v1.4. These shapes mirror the API's onboarding lifecycle
 * slice and are validated on every response.
 *
 * WHAT THIS SURFACE IS
 * --------------------
 * The tenant onboarding lifecycle: a tenant OWNER submits its onboarding for
 * review, a platform operator reviews it (approve / reject / request more
 * information), and an approved submission is activated. The readiness
 * projection is DERIVED and read-only; this surface is the state machine that
 * records what happened to it.
 *
 * TWO SCOPES
 * ----------
 *   - TENANT-scoped (owner): submit + read the current tenant's submission.
 *     The tenant is resolved from the caller's own owner membership, never a
 *     path parameter.
 *   - PLATFORM-scoped (`tenant.update`): review + activate a NAMED tenant's
 *     submission, selected by path parameter.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from '@/api/schemas/common';

// ---------------------------------------------------------------------------
// Shared vocabularies (mirror the database CHECK constraints)
// ---------------------------------------------------------------------------

export const OnboardingCategoryStatusSchema = z.enum(['complete', 'missing', 'not_supported']);
export type OnboardingCategoryStatus = z.infer<typeof OnboardingCategoryStatusSchema>;

export const OnboardingSubmissionStatusSchema = z.enum([
  'submitted',
  'approved',
  'rejected',
  'more_info_required',
  'activated',
]);
export type OnboardingSubmissionStatus = z.infer<typeof OnboardingSubmissionStatusSchema>;

export const OnboardingReviewDecisionSchema = z.enum([
  'approved',
  'rejected',
  'more_info_required',
]);
export type OnboardingReviewDecision = z.infer<typeof OnboardingReviewDecisionSchema>;

// ---------------------------------------------------------------------------
// The readiness snapshot (frozen at submit time)
// ---------------------------------------------------------------------------

export const ReadinessCategorySchema = z
  .object({
    category: z.string().min(1).max(64),
    status: OnboardingCategoryStatusSchema,
    reason: z.string().max(500).optional(),
  })
  .strict();
export type ReadinessCategory = z.infer<typeof ReadinessCategorySchema>;

export const ReadinessSnapshotSchema = z
  .object({
    overall: z.enum(['complete', 'incomplete']),
    missingCount: z.number().int().min(0),
    categories: z.array(ReadinessCategorySchema).max(64),
  })
  .strict();
export type ReadinessSnapshot = z.infer<typeof ReadinessSnapshotSchema>;

// ---------------------------------------------------------------------------
// The submission document
// ---------------------------------------------------------------------------

export const OnboardingSubmissionSchema = z
  .object({
    id: UuidSchema,
    tenantId: UuidSchema,
    status: OnboardingSubmissionStatusSchema,
    readinessSnapshot: ReadinessSnapshotSchema,
    submittedBy: UuidSchema,
    submittedAt: IsoDateTimeSchema,
    reviewedBy: UuidSchema.nullable(),
    reviewedAt: IsoDateTimeSchema.nullable(),
    decisionReason: z.string().nullable(),
    activatedAt: IsoDateTimeSchema.nullable(),
    version: z.number().int().min(1),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
  })
  .strict();
export type OnboardingSubmission = z.infer<typeof OnboardingSubmissionSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/** Submit the tenant's onboarding for review. The snapshot is the readiness read's output. */
export const SubmitOnboardingRequestSchema = z
  .object({
    readinessSnapshot: ReadinessSnapshotSchema,
  })
  .strict();
export type SubmitOnboardingRequest = z.infer<typeof SubmitOnboardingRequestSchema>;

/**
 * A platform review decision. `reason` is REQUIRED for `rejected` and
 * `more_info_required` and MUST be omitted for `approved` — the API function
 * enforces the same rule.
 */
export const ReviewOnboardingRequestSchema = z
  .object({
    decision: OnboardingReviewDecisionSchema,
    reason: z.string().min(1).max(2000).optional(),
    expectedVersion: z.number().int().min(1),
  })
  .strict();
export type ReviewOnboardingRequest = z.infer<typeof ReviewOnboardingRequestSchema>;

/** Activate an approved submission. */
export const ActivateOnboardingRequestSchema = z
  .object({
    expectedVersion: z.number().int().min(1),
  })
  .strict();
export type ActivateOnboardingRequest = z.infer<typeof ActivateOnboardingRequestSchema>;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

const MetaSchema = z.object({ requestId: z.string().max(128) }).strict();

/** The submit / review / activate success envelope. */
export const OnboardingSubmissionSuccessResponseSchema = z
  .object({ data: OnboardingSubmissionSchema, meta: MetaSchema })
  .strict();
export type OnboardingSubmissionSuccessResponse = z.infer<
  typeof OnboardingSubmissionSuccessResponseSchema
>;

/** The tenant-scoped read envelope; `data` is null when the tenant never submitted. */
export const OnboardingSubmissionReadResponseSchema = z
  .object({ data: OnboardingSubmissionSchema.nullable(), meta: MetaSchema })
  .strict();
export type OnboardingSubmissionReadResponse = z.infer<
  typeof OnboardingSubmissionReadResponseSchema
>;

/** The closed error-code vocabulary for this surface. */
export const OnboardingLifecycleErrorCodeSchema = z.enum([
  'INVALID_ONBOARDING_INPUT',
  'ONBOARDING_INCOMPLETE',
  'ONBOARDING_ALREADY_SUBMITTED',
  'ONBOARDING_NOT_FOUND',
  'ONBOARDING_BAD_TRANSITION',
  'ONBOARDING_VERSION_CONFLICT',
  'IDEMPOTENCY_KEY_REQUIRED',
  'IDEMPOTENCY_KEY_REUSED',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'SERVICE_UNAVAILABLE',
]);
export type OnboardingLifecycleErrorCode = z.infer<typeof OnboardingLifecycleErrorCodeSchema>;
